import React, { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Table, Card, Button, Space, Tag } from "antd"
import { gameNames } from "abstract/constants/game-ids"
import { CreateEditFormulaModal } from "./create-edit-formula-modal"
import { TestFormulaModal } from "./test-formula-modal"
import { Formula } from "../store/types"
import { showDeleteConfirm, GameAutoComplete } from "components"
import { notifications } from "notifications"

export const FormulasTab: FC = observer(() => {
  const { dataStore: { formulasStore } } = useStore()
  const [gameInputValue, setGameInputValue] = useState<string>("")
  const [showModal, setShowModal] = useState(false)
  const [editingFormula, setEditingFormula] = useState<Formula | null>(null)
  const [showTestModal, setShowTestModal] = useState(false)
  const [testingFormula, setTestingFormula] = useState<Formula | null>(null)

  useEffect(() => {
    formulasStore.getAllFormulas()
  }, [formulasStore])

  const handleGameChange = (value: string) => {
    setGameInputValue(value)
  }

  const handleGameApply = (value: string, gameId: string | null) => {
    if (gameId) {
      formulasStore.getFormulasByGame(gameId)
    } else if (!value) {
      handleGameClear()
    }
  }

  const handleGameClear = () => {
    setGameInputValue("")
    formulasStore.clearGameFilter()
    formulasStore.getAllFormulas()
  }

  const handleDelete = (formula: Formula) => {
    showDeleteConfirm(
      `formula ${formula.id}`,
      undefined,
      async() => {
        try {
          await formulasStore.deleteFormula(formula.id)
          notifications.success("Formula deleted")
        } catch (error: any) {
          notifications.error(error?.cause?.message || error?.message || "Failed to delete formula")
        }
      },
    )
  }

  const columns = [
    {
      title: "ID",
      key: "id",
      dataIndex: "id",
      width: 200,
    },
    {
      title: "Game ID",
      key: "gameId",
      dataIndex: "gameId",
      width: 200,
      render: (value: number) => {
        if (!value) return "-"

        const gameName = gameNames[value]

        return gameName ? `${value} - ${gameName}` : value
      },
    },
    {
      title: "Game Mode",
      key: "gameMode",
      dataIndex: "gameMode",
      width: 150,
      render: (value: string | null | undefined) => (
        <Tag color={value ? "blue" : "default"}>
          {value ?? "Global"}
        </Tag>
      ),
    },
    {
      title: "Is Active",
      key: "isActive",
      dataIndex: "isActive",
      width: 100,
      render: (value: boolean | undefined) => (
        <Tag color={value ? "green" : "red"}>
          {value ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Formula",
      key: "formula",
      dataIndex: "formula",
      ellipsis: true,
    },
    {
      title: "Apply Min/Max Clamp",
      key: "applyMinMaxClamp",
      dataIndex: "applyMinMaxClamp",
      width: 150,
      render: (value: boolean) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Min Reward",
      key: "minReward",
      dataIndex: "minReward",
      width: 120,
      render: (value: number | undefined) => value ?? "-",
    },
    {
      title: "Max Reward",
      key: "maxReward",
      dataIndex: "maxReward",
      width: 120,
      render: (value: number | undefined) => value ?? "-",
    },
    {
      title: "Apply Victory Bonus",
      key: "applyVictoryBonus",
      dataIndex: "applyVictoryBonus",
      width: 150,
      render: (value: boolean | undefined) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Victory Bonus Value",
      key: "victoryBonusValue",
      dataIndex: "victoryBonusValue",
      width: 150,
      render: (value: number | undefined) => value ?? "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right" as const,
      render: (_: any, record: Formula) => (
        <Space>
          <Button
            type="link"
            onClick={e => {
              e.stopPropagation()
              setTestingFormula(record)
              setShowTestModal(true)
            }}
          >
            Test
          </Button>
          <Button
            type="link"
            danger
            onClick={e => {
              e.stopPropagation()
              handleDelete(record)
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ]


  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Button
            type="primary"
            onClick={() => {
              setEditingFormula(null)
              setShowModal(true)
            }}
            style={{ marginBottom: 16 }}
          >
            Create Formula
          </Button>
          <Card title="List All Formulas" size="small">
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Space wrap>
                <GameAutoComplete
                  value={gameInputValue}
                  onChange={handleGameChange}
                  onApply={handleGameApply}
                  onClear={handleGameClear}
                  applyOnBlur={true}
                />
                <Button
                  type="primary"
                  onClick={() => {
                    const gameId = gameInputValue.includes(" - ")
                      ? gameInputValue.split(" - ")[0]
                      : gameInputValue.trim()
                    handleGameApply(gameInputValue, gameId || null)
                  }}
                >
                  Filter
                </Button>
                {formulasStore.currentGameId && (
                  <Button
                    onClick={handleGameClear}
                  >
                    Clear Filter
                  </Button>
                )}
              </Space>
              <Table
                dataSource={Array.isArray(formulasStore.displayedFormulas) ? formulasStore.displayedFormulas : []}
                columns={columns}
                rowKey="id"
                loading={formulasStore.isLoading}
                pagination={{ pageSize: 20 }}
                scroll={{ x: "max-content" }}
                onRow={record => ({
                  style: { cursor: "pointer" },
                  onClick: () => {
                    setEditingFormula(record)
                    setShowModal(true)
                  },
                })}
              />
            </Space>
          </Card>
        </div>
      </Space>
      <CreateEditFormulaModal
        visible={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingFormula(null)
        }}
        initialData={editingFormula}
      />
      <TestFormulaModal
        visible={showTestModal}
        onClose={() => {
          setShowTestModal(false)
          setTestingFormula(null)
        }}
        formula={testingFormula}
      />
    </div>
  )
})
